import { useCallback, useEffect, useRef } from 'react';

import intercom, {
  hide,
  show,
  showNewMessage,
  shutdown,
} from '@intercom/messenger-js-sdk';

import { useAuth } from '../../behaviors';

const INTERCOM_APP_ID = 'qsl2vvw9';

interface UseIntercomOptions {
  enabled?: boolean;
}

interface IntercomIdentity {
  userId: string;
  userHash: string;
}

async function fetchIntercomIdentityHash(
  accessToken: string,
): Promise<IntercomIdentity | undefined> {
  try {
    const response = await fetch(
      'https://onboarding.confidence.dev/v1/intercom/identity',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    if (!response.ok) return undefined;
    return await response.json();
  } catch {
    return undefined;
  }
}

export const intercomSingleton = (() => {
  let consumers = 0;

  return {
    acquire() {
      if (consumers === 0) {
        intercom({
          app_id: INTERCOM_APP_ID,
          hide_default_launcher: true,
        });
      }
      consumers++;
    },
    release() {
      consumers--;
      if (consumers === 0) {
        shutdown();
      }
    },
  };
})();

export const useIntercom = ({ enabled = true }: UseIntercomOptions = {}) => {
  const identityCacheRef = useRef<IntercomIdentity>();
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    intercomSingleton.acquire();

    return () => {
      intercomSingleton.release();
    };
  }, [enabled]);

  const ensureIdentity = useCallback(
    async (user: { name: string; email: string }) => {
      let identity = identityCacheRef.current;
      if (!identity) {
        try {
          const token = await getAccessTokenSilently();
          identity = await fetchIntercomIdentityHash(token);
          if (identity) {
            identityCacheRef.current = identity;
          }
        } catch {
          // Fall through without identity verification
        }
      }

      intercom({
        app_id: INTERCOM_APP_ID,
        hide_default_launcher: true,
        user_id: identity?.userId ?? user.email,
        name: user.name,
        email: user.email,
        ...(identity?.userHash && { user_hash: identity.userHash }),
      });
    },
    [getAccessTokenSilently],
  );

  const openChat = useCallback(
    async (user?: { name: string; email: string }) => {
      if (!enabled) return;
      if (user) await ensureIdentity(user);
      show();
    },
    [enabled, ensureIdentity],
  );

  const closeChat = useCallback(() => {
    if (!enabled) return;
    hide();
  }, [enabled]);

  const toggleChat = useCallback(() => {
    if (!enabled) return;
    show();
  }, [enabled]);

  const openChatWithMessage = useCallback(
    async (message: string, user?: { name: string; email: string }) => {
      if (!enabled) return;
      if (user) await ensureIdentity(user);
      showNewMessage(message);
    },
    [enabled, ensureIdentity],
  );

  return {
    openChat,
    closeChat,
    toggleChat,
    openChatWithMessage,
  };
};
